import { useState } from 'react'
import { Dialog } from '../ui/Dialog'
import { useStation } from '../state/TelephonyProvider'
import { useCommand } from '../state/useCommand'
import { TelephonyCommandNames, type CallForwardKind } from '../contract'

const KINDS: Array<{ id: CallForwardKind; label: string }> = [
  { id: 'always', label: 'Always' },
  { id: 'busy', label: 'When busy' },
  { id: 'noanswer', label: 'No answer' }
]

// Call-forward settings for the bound station. Set is edge-ready; Get is a pending handler
// (will report "not wired up yet" if the operator reads current settings).
export function CallForwardDialog({ onClose }: { onClose: () => void }) {
  const run = useCommand()
  const station = useStation()
  const [kind, setKind] = useState<CallForwardKind>('always')
  const [enabled, setEnabled] = useState(true)
  const [dest, setDest] = useState('')

  const canSave = !enabled || dest.trim().length > 0

  const save = async () => {
    if (!canSave) return
    const res = await run(
      {
        command: TelephonyCommandNames.SetCallForward,
        params: { extension: station.extension, kind, enabled, destination: dest.trim() || undefined }
      },
      { successMessage: `Call forward (${kind}) ${enabled ? `on → ${dest.trim()}` : 'off'}` }
    )
    if (res && res.status === 'success') onClose()
  }

  return (
    <Dialog
      title={`Call forwarding — ext ${station.extension}`}
      onClose={onClose}
      footer={
        <>
          <button className="sb-btn-ghost" onClick={() => void run({ command: TelephonyCommandNames.GetCallForward, params: { extension: station.extension } })}>
            Read current
          </button>
          <span style={{ flex: 1 }} />
          <button className="sb-btn-ghost" onClick={onClose}>Cancel</button>
          <button className="sb-btn-primary" disabled={!canSave} onClick={() => void save()}>Save</button>
        </>
      }
    >
      <div className="sb-field">
        <label>Condition</label>
        <div className="sb-seg" role="group" aria-label="Forward condition">
          {KINDS.map((k) => (
            <button key={k.id} className={kind === k.id ? 'on' : ''} onClick={() => setKind(k.id)}>{k.label}</button>
          ))}
        </div>
      </div>
      <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
        <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
        Forwarding enabled
      </label>
      <div className="sb-field">
        <label>Forward to</label>
        <input
          className="sb-input" inputMode="tel" placeholder="Extension or number"
          value={dest} onChange={(e) => setDest(e.target.value)} disabled={!enabled}
        />
      </div>
      <p className="sb-note">Sets the {kind} forward for your station. Disable to clear it.</p>
    </Dialog>
  )
}
